import { askGPT } from '../components/OpenAIService';
import { makeAuthenticatedRequest } from '../components/APIService';
import JinaAIExtract from '../components/JinaAIExtract';

// Configuration de l'URL du backend
const BackEndUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';

/**
 * Service pour générer des analyses détaillées des sources
 * Architecture simplifiée : Perplexity → JinaAI → GPT fallback
 */
class SourceAnalysisService {
  /**
   * Génère un résumé détaillé d'une source en fonction de l'objectif de recherche
   * Workflow optimisé en 3 niveaux :
   * 1. Perplexity (extraction + synthèse via API backend)
   * 2. JinaAI (extraction de contenu réel)
   * 3. GPT (fallback avec métadonnées uniquement)
   * 
   * @param {Object} source - La source à analyser
   * @param {string} searchObjective - L'objectif de la recherche
   * @param {string} userLanguage - La langue de l'utilisateur
   * @returns {Promise<string>} - Le résumé détaillé
   */
  static async generateDetailedSummary(source, searchObjective, userLanguage = 'fr') {
    try {
      console.log('🔍 Analyse détaillée pour:', source.title);
      
      // NIVEAU 1 : Essayer Perplexity (priorité maximale)
      try {
        console.log('🚀 Tentative avec Perplexity...');
        const perplexityResult = await this.extractWithPerplexity(source, searchObjective, userLanguage);
        
        if (perplexityResult) {
          console.log('✅ Analyse générée avec succès via Perplexity');
          return perplexityResult;
        }
      } catch (perplexityError) {
        console.warn('⚠️ Perplexity non disponible:', perplexityError.message);
      }
      
      // NIVEAU 2 : Essayer JinaAI (fallback intelligent)
      try {
        console.log('🔧 Tentative avec JinaAI...');
        const jinaResult = await this.extractWithJinaAI(source, searchObjective, userLanguage);
        
        if (jinaResult) {
          console.log('✅ Analyse générée avec succès via JinaAI');
          return jinaResult;
        }
      } catch (jinaError) {
        console.warn('⚠️ JinaAI non disponible:', jinaError.message);
      }
      
      // Aucun service n'a réussi - retourner null
      console.error('❌ Impossible de générer l\'analyse : Perplexity et JinaAI ont échoué');
      return null;
      
    } catch (error) {
      console.error('❌ Erreur lors de la génération du résumé détaillé:', error);
      return null;
    }
  }

  /**
   * NIVEAU 1 : Extraction et analyse avec Perplexity
   * Utilise l'API backend /api/synthesis/generate
   * Retourne directement la synthèse sans post-traitement
   */
  static async extractWithPerplexity(source, searchObjective, userLanguage) {
    // Appeler la route /api/search/synthesis/generate avec l'URL complète du backend
    const response = await makeAuthenticatedRequest(
      `${BackEndUrl}/api/search/synthesis/generate`,
      {
        method: 'POST',
        body: JSON.stringify({
          sources: [source],
          userObjective: searchObjective,
          options: {
            language: userLanguage,
            detailedAnalysis: true,
            singleSource: true // Indiquer qu'on analyse une seule source
          }
        })
      }
    );
    
    // Parser la réponse JSON
    const data = await response.json();
    
    if (!data || !data.synthesis) {
      throw new Error('Réponse Perplexity invalide');
    }
    
    // Extraire le contenu de la synthèse
    const synthesisContent = data.synthesis.content || data.synthesis;
    
    // Formater pour l'affichage
    return this.formatPerplexitySynthesis(synthesisContent, source, searchObjective, userLanguage);
  }

  /**
   * Formater la synthèse Perplexity pour l'affichage
   */
  static formatPerplexitySynthesis(content, source, searchObjective, userLanguage) {
    // Si le contenu est déjà bien formaté, le retourner tel quel
    if (content.includes('##') || content.includes('**')) {
      return content;
    }
    
    // Sinon, ajouter une structure minimale
    return `## Analyse de la source

**Source :** ${source.title}
**URL :** ${source.url}
**Objectif :** ${searchObjective}

---

${content}

---

*Analyse générée par Perplexity*`;
  }

  /**
   * NIVEAU 2 : Extraction et analyse avec JinaAI
   * Extrait le contenu réel de la page puis analyse avec GPT
   */
  static async extractWithJinaAI(source, searchObjective, userLanguage) {
    // Créer une instance JinaAI sans auto-save
    const jinaExtractor = new JinaAIExtract({
      useGPTPostProcessing: false, // On fera notre propre post-traitement
      autoSave: false
    });
    
    // Extraire le contenu de la source
    const extractionResult = await jinaExtractor.findAnswer(
      searchObjective,
      source.url,
      {
        useGPTPostProcessing: false,
        contextLines: 10
      }
    );
    
    if (!extractionResult.success || !extractionResult.content) {
      throw new Error('Extraction JinaAI échouée');
    }
    
    console.log(`📄 Contenu extrait par JinaAI: ${extractionResult.content.length} caractères`);
    
    // Analyser le contenu extrait avec GPT
    return await this.analyzeExtractedContent(
      source,
      searchObjective,
      extractionResult.content,
      userLanguage
    );
  }

  /**
   * Analyser le contenu extrait par JinaAI avec GPT
   */
  static async analyzeExtractedContent(source, searchObjective, extractedContent, userLanguage) {
    const prompt = `Tu es un expert en analyse de sources d'information. Analyse le contenu extrait suivant et génère un résumé détaillé en ${userLanguage}.

**Objectif de la recherche :** ${searchObjective}

**Source :**
- Titre : ${source.title}
- URL : ${source.url}
- Domaine : ${source.domain || 'Non spécifié'}

**Contenu extrait de la page web :**
${extractedContent}

**Instructions :**
1. Explique en détail pourquoi cette source est pertinente pour l'objectif de recherche
2. Résume TOUTES les informations importantes trouvées dans le contenu
3. Identifie les concepts clés, données, statistiques, et références importantes
4. Structure le résumé de manière claire avec des sections
5. Réponds UNIQUEMENT en ${userLanguage}

**Format attendu :**
## Pertinence pour l'objectif
[Explication détaillée de la pertinence]

## Informations clés
[Liste structurée des informations importantes extraites]

## Concepts et données
[Concepts clés, statistiques et données chiffrées]

## Points saillants
[Éléments remarquables ou insights particuliers]

## Conclusion
[Synthèse de la valeur de cette source pour l'objectif]`;

    const response = await askGPT(prompt);
    return response;
  }

  /**
   * Génère une analyse complète pour un document séparé
   * @param {Object} source - La source à analyser
   * @param {string} searchObjective - L'objectif de la recherche
   * @param {string} userLanguage - La langue de l'utilisateur
   * @returns {Promise<Object>} - L'analyse complète structurée
   */
  static async generateFullAnalysis(source, searchObjective, userLanguage = 'fr') {
    try {
      const prompt = `Tu es un expert en analyse documentaire. Crée une analyse complète et approfondie de cette source en ${userLanguage}.

Objectif de la recherche : ${searchObjective}

Source :
- Titre : ${source.title}
- URL : ${source.url}
- Contenu : ${source.content || source.snippet || 'Non disponible'}

Crée une analyse structurée et détaillée incluant :

1. **Résumé exécutif** : Vue d'ensemble de la source et de sa pertinence
2. **Analyse de pertinence** : Pourquoi cette source est importante pour l'objectif
3. **Contenu détaillé** : Extraction complète des informations pertinentes
4. **Concepts clés** : Identification et explication des concepts importants
5. **Données et statistiques** : Toutes les données chiffrées pertinentes
6. **Références et citations** : Sources citées dans le document
7. **Points forts et limites** : Analyse critique de la source
8. **Applications pratiques** : Comment utiliser ces informations
9. **Recommandations** : Suggestions pour approfondir le sujet

Réponds UNIQUEMENT en ${userLanguage} avec un format Markdown structuré.`;

      const response = await askGPT(prompt);
      
      return {
        title: source.title,
        url: source.url,
        objective: searchObjective,
        analysis: response,
        generatedAt: new Date().toISOString(),
        language: userLanguage
      };
    } catch (error) {
      console.error('Erreur lors de la génération de l\'analyse complète:', error);
      throw error;
    }
  }

  /**
   * Traduit un résumé dans la langue de l'utilisateur si nécessaire
   * @param {string} summary - Le résumé à traduire
   * @param {string} targetLanguage - La langue cible
   * @returns {Promise<string>} - Le résumé traduit
   */
  static async translateSummary(summary, targetLanguage) {
    try {
      const prompt = `Traduis le texte suivant en ${targetLanguage}. Conserve la structure et le formatage Markdown.

Texte à traduire :
${summary}`;

      const response = await askGPT(prompt);
      return response;
    } catch (error) {
      console.error('Erreur lors de la traduction:', error);
      return summary; // Retourne le résumé original en cas d'erreur
    }
  }
}

export default SourceAnalysisService;
